import type { DataSourceSettings } from '../types';

interface ToggleRow {
  key: keyof DataSourceSettings;
  label: string;
  description: string;
}

const TOGGLES: ToggleRow[] = [
  {
    key: 'bitmexRest',
    label: 'Server candle refresh',
    description: 'Enable backend candle refreshes from the active market data provider.',
  },
  {
    key: 'bitmexLatestTrade',
    label: 'Live price overlay',
    description: 'Enable latest trade price overlays when the current provider supports them.',
  },
  {
    key: 'bitmexWebsocket',
    label: 'Live stream updates',
    description: 'Enable websocket streaming when the current symbol supports live updates.',
  },
  {
    key: 'backendCache',
    label: 'Backend SQLite cache',
    description: 'Enable server-side cache reads and writes from data/cache.sqlite3.',
  },
  {
    key: 'localStorageCache',
    label: 'Browser localStorage cache',
    description: 'Enable frontend candle persistence in localStorage.',
  },
];

interface DataSourceSettingsPanelProps {
  settings: DataSourceSettings;
  onChange: (next: DataSourceSettings) => void;
  onReset?: () => void;
  onHardReload?: () => void;
  showFooterActions?: boolean;
}

export function DataSourceSettingsPanel({
  settings,
  onChange,
  onReset,
  onHardReload,
  showFooterActions = true,
}: DataSourceSettingsPanelProps) {
  return (
    <>
      <div className="space-y-3 p-4">
        {TOGGLES.map((toggle) => (
          <label
            key={toggle.key}
            className="flex items-start justify-between gap-3 rounded-lg border border-slate-700/80 bg-slate-950/35 px-3 py-3"
          >
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-100">{toggle.label}</p>
              <p className="text-xs text-slate-400">{toggle.description}</p>
            </div>
            <input
              type="checkbox"
              checked={settings[toggle.key]}
              onChange={(event) =>
                onChange({
                  ...settings,
                  [toggle.key]: event.target.checked,
                })
              }
              className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900"
            />
          </label>
        ))}
      </div>

      {showFooterActions ? (
        <div className="flex items-center justify-between border-t border-slate-700 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onReset}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 transition-colors hover:bg-slate-700"
            >
              Reset defaults
            </button>
            <div>
              <button
                type="button"
                onClick={onHardReload}
                className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-200 transition-colors hover:bg-amber-500/20"
              >
                Hard Reload Candles
              </button>
              <p className="mt-1 text-[11px] text-slate-400">
                Clears browser candle cache for this symbol and forces one fresh fetch without backend cache.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
